import { WalletDesignLayout, WalletDesignRotation } from './wallet-design';

export const STRIP_SIZE: [number, number] = [1125, 369];
export const SQUARE_SIZE = 660;

type Box = [number, number, number, number];

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function rgb(hex: string): [number, number, number] {
  const value = hex.replace('#', '');
  return [0, 2, 4].map(offset => parseInt(value.substr(offset, 2), 16)) as [number, number, number];
}

export function luminance(hex: string): number {
  const weights = [0.2126, 0.7152, 0.0722];
  return rgb(hex)
    .map(c => c / 255)
    .map(c => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4))
    .reduce((sum, c, index) => sum + c * weights[index], 0);
}

export function contrast(first: string, second: string): number {
  const [lighter, darker] = [luminance(first), luminance(second)].sort((a, b) => b - a);
  return (lighter + 0.05) / (darker + 0.05);
}

export function googleTextColor(background: string): string {
  return luminance(background) > 0.4 ? '#1f1f1f' : '#ffffff';
}

export function loadImage(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = source;
  });
}

export function rotate(image: HTMLImageElement, rotation: WalletDesignRotation): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const quarter = rotation === 90 || rotation === 270;
  canvas.width = quarter ? image.height : image.width;
  canvas.height = quarter ? image.width : image.height;
  const context = canvas.getContext('2d');
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate(rotation * Math.PI / 180);
  context.drawImage(image, -image.width / 2, -image.height / 2);
  return canvas;
}

function filledCanvas(width: number, height: number, background: string): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  context.fillStyle = background;
  context.fillRect(0, 0, width, height);
  return canvas;
}

function artworkStrip(card: HTMLCanvasElement, box: Box, zoom: number, background: string): HTMLCanvasElement {
  const [left, top, width, height] = box;
  const aspect = STRIP_SIZE[0] / STRIP_SIZE[1];
  const padding = Math.round(height * 0.12);
  const regionHeight = Math.min(Math.round((height + 2 * padding) / zoom), card.height);
  const regionWidth = Math.min(Math.max(Math.round(regionHeight * aspect), Math.round((width + 2 * padding) / zoom)), card.width);
  const x = clamp(left + Math.floor(width / 2) - Math.floor(regionWidth / 2), 0, card.width - regionWidth);
  const y = clamp(top + Math.floor(height / 2) - Math.floor(regionHeight / 2), 0, card.height - regionHeight);
  const targetHeight = Math.max(Math.round(regionWidth / aspect), regionHeight);
  const targetWidth = Math.round(targetHeight * aspect);
  const scale = STRIP_SIZE[0] / targetWidth;
  const strip = filledCanvas(STRIP_SIZE[0], STRIP_SIZE[1], background);
  strip.getContext('2d').drawImage(card, x, y, regionWidth, regionHeight,
    Math.floor((targetWidth - regionWidth) / 2) * scale, Math.floor((targetHeight - regionHeight) / 2) * scale,
    regionWidth * scale, regionHeight * scale);
  return strip;
}

function centeredStrip(card: HTMLCanvasElement, box: Box, zoom: number, background: string): HTMLCanvasElement {
  const [left, top, width, height] = box;
  const padding = Math.round(height * 0.12);
  const x = Math.max(left - padding, 0);
  const y = Math.max(top - padding, 0);
  const elementWidth = Math.min(width + 2 * padding, card.width - x);
  const elementHeight = Math.min(height + 2 * padding, card.height - y);
  const scale = Math.min(STRIP_SIZE[1] * 0.8 / elementHeight, STRIP_SIZE[0] * 0.9 / elementWidth) * zoom;
  const strip = filledCanvas(STRIP_SIZE[0], STRIP_SIZE[1], background);
  strip.getContext('2d').drawImage(card, x, y, elementWidth, elementHeight,
    (STRIP_SIZE[0] - elementWidth * scale) / 2, (STRIP_SIZE[1] - elementHeight * scale) / 2,
    elementWidth * scale, elementHeight * scale);
  return strip;
}

export function renderStrip(card: HTMLCanvasElement, box: Box, layout: WalletDesignLayout,
                            zoom: number, background: string): string {
  if (layout === 'colors_only') {
    return null;
  }
  const strip = layout === 'artwork' ? artworkStrip(card, box, zoom, background) : centeredStrip(card, box, zoom, background);
  return strip.toDataURL('image/png');
}

export function renderSquareLogo(card: HTMLCanvasElement, box: Box, background: string): string {
  const [left, top, width, height] = box;
  const scale = SQUARE_SIZE * 0.7 / Math.max(width, height);
  const square = filledCanvas(SQUARE_SIZE, SQUARE_SIZE, background);
  square.getContext('2d').drawImage(card, left, top, width, height,
    (SQUARE_SIZE - width * scale) / 2, (SQUARE_SIZE - height * scale) / 2, width * scale, height * scale);
  return square.toDataURL('image/png');
}
